import * as angular from 'angular';
import * as d3 from 'd3';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ILoggerService, IDateRangeService, IRequestService, ISeriesService } from '../../services';
import { ActualDataType, IDisplayedColumn, IMappingDictionary, IValueViewModel, SeriesDefinition, TagDateValue } from '../../models';
import { GetAggregatedValueDto } from '../../models/DTO';
import { IFormatNumberHelper, IUploadService } from '../helpers';
import { getTimeZone } from '../../common/getTimezone';
import { formatValueForCsv } from './formatValueForCsv';
import { format } from '../../common/helpers/timestampDataType/constants';

const Csvformat = 'YYYY-MM-DD HH:mm';

export interface CsvExportOptions {
    hasFirstHeading: boolean;
    hasGroupName: boolean;
    hasLegend: boolean;
    hasShortComment: boolean;
    hasUnit: boolean;
    hasValue: boolean;
}

interface SimpleCsvSeries extends SeriesDefinition<TagDateValue> {
    attributes?: any;
    identifier: string;
    legendText?: string;
    selector: (d: any) => string;
}

interface CsvSeries extends SimpleCsvSeries {
    serieType?: string;
}

export interface IExportToCsvService {
    getCsv(configuration: any[], fileNamePrefix?: string, guid?: string, includeLogs?: boolean): angular.IPromise<any>;
    getCsvPercentageGoodValuesPerDate(fileNamePrefix, guid?: string): angular.IPromise<any>;
    getCsvPercentageBadValuesPerDate(fileNamePrefix, guid?: string): angular.IPromise<any>;
    getCsvBadValuesPerTag(fileNamePrefix, guid?: string): angular.IPromise<any>;
    getCsvPercentageFlatPerDate(fileNamePrefix, guid?: string): angular.IPromise<any>;
    getCsvPercentageScatterPerDate(fileNamePrefix, guid?: string): angular.IPromise<any>;
    getCsvPercentageOutOfRangePerDate(fileNamePrefix, guid?: string): angular.IPromise<any>;
    getCsvPercentageSuspiciousKpisPerDate(fileNamePrefix, guid?: string): angular.IPromise<any>;
    getCsvPercentageInvalidKpisPerDate(fileNamePrefix, guid?: string): angular.IPromise<any>;
    getCsvGroupStates(groupGuid: string, beginDate: string, endDate: string): angular.IPromise<string>;
    getCsvAggregatedValue(aggregatedValueDto: GetAggregatedValueDto, name: string): angular.IPromise<string>;
    getCsvFromData(data: any[][], name: string, addDoubleQuotes?: boolean);
    exportAggregatedToCsv(series: SimpleCsvSeries[], boundData: TagDateValue[], name: string): angular.IPromise<any>;
    exportToCsv(series: CsvSeries[], boundData: TagDateValue[], name: string, options?: CsvExportOptions): angular.IPromise<any>;
    getCsvContent(columns: IDisplayedColumn[], data: any[], propertyMapper: IMappingDictionary<any>, isGuidNotExported?: boolean): string[][];
    hasExportCustomProperties(exportedFields: IDisplayedColumn[]): boolean;
    getUserFieldsHeaders(data: { customProperties?: any }[]): string[];
    getUserFieldsValues(customProperties: { [key: string]: string }): string[];
}

export class ExportToCsvService implements IExportToCsvService {
    static serviceId = 'exportToCsvService';

    private _controllerUrl: string;
    private _timeZone: string;

    static $inject = [
        '$q',
        'loggerService',
        'dateRangeService',
        'formatNumberHelper',
        'requestService',
        'seriesService',
        'uploadService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _loggerService: ILoggerService,
        private _dateRangeService: IDateRangeService,
        private _formatNumberHelper: IFormatNumberHelper,
        private _requestService: IRequestService,
        private _seriesService: ISeriesService,
        private _uploadService: IUploadService
    ) {
        this._timeZone = getTimeZone();
    }


    private controllerUrl() {
        if (!this._controllerUrl) {
            this._controllerUrl = this._requestService.getUrlV1() + 'ExportToCsv/';
        }
        return this._controllerUrl;
    }

    getCsv(configuration: any[],
        fileNamePrefix?: string,
        guid?: string,
        includeLogs = false): angular.IPromise<any> {
        return this.getAtAddress('GetCsv', fileNamePrefix, guid, configuration, includeLogs);
    }

    getCsvPercentageGoodValuesPerDate(fileNamePrefix, guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetCsvPercentageGoodValuesPerDate', fileNamePrefix, guid);
    }

    getCsvPercentageBadValuesPerDate(fileNamePrefix, guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetCsvPercentageBadValuesPerDate', fileNamePrefix, guid);
    }

    getCsvBadValuesPerTag(fileNamePrefix, groupGuid: string): angular.IPromise<any> {
        return this.getAtAddress('GetCsvBadValuesPerTag', fileNamePrefix, groupGuid);
    }

    getCsvPercentageFlatPerDate(fileNamePrefix, guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetCsvPercentageFlatPerDate', fileNamePrefix, guid);
    }

    getCsvPercentageScatterPerDate(fileNamePrefix, guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetCsvPercentageScatterPerDate', fileNamePrefix, guid);
    }

    getCsvPercentageOutOfRangePerDate(fileNamePrefix, guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetCsvPercentageOutOfRangePerDate', fileNamePrefix, guid);
    }

    getCsvPercentageSuspiciousKpisPerDate(fileNamePrefix, guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetCsvPercentageSuspiciousKpisPerDate', fileNamePrefix, guid);
    }

    getCsvPercentageInvalidKpisPerDate(fileNamePrefix, guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetCsvPercentageInvalidKpisPerDate', fileNamePrefix, guid);
    }

    private getAtAddress(method: string,
        fileNamePrefix: string,
        guid?: string,
        configuration?: any[],
        includeLogs = false): angular.IPromise<any> {
        const defered = this._$q.defer();
        const beginDate = this._dateRangeService.beginDate() as string;
        const endDate = this._dateRangeService.endDate() as string;
        if (!fileNamePrefix) {
            fileNamePrefix = 'csv';
        }
        const url = this.controllerUrl() + method
            + '?fileNamePrefix=' + fileNamePrefix
            + (guid ? `&groupGuid=${guid}` : '')
            + '&beginDateTime=' + beginDate
            + '&endDateTime=' + endDate
            + '&includeLogs=' + includeLogs
            + '&localTimeZone=' + encodeURIComponent(this._timeZone);
        this._requestService.cleanCache(url);
        const apiUrl = this._requestService.getApiUrl();
        if (configuration) {
            const bound = configuration.filter((c: any) => c.isBoundToGroup);
            this._seriesService
                .getUpdatedGuids(bound)
                .then(data => {

                    return this._requestService.post(url, data.concat(configuration.filter((c: any) => !c.isBoundToGroup)));
                })
                .then((data) => {
                    this.tryOpen(defered, apiUrl, data);
                });
        } else {
            this._requestService
                .post(url, configuration)
                .then((data) => {
                    this.tryOpen(defered, apiUrl, data);
                });
        }
        return defered.promise;
    }

    private tryOpen(defered: angular.IDeferred<unknown>, apiUrl: string, data: any): void {
        try {
            window.open(apiUrl + data);
            defered.resolve();
        } catch (e) {
            defered.reject();
        }
    }

    getCsvGroupStates(groupGuid: string, beginDate: string, endDate: string): angular.IPromise<string> {
        const defered = this._$q.defer<string>();
        const url = this.controllerUrl() + 'GetCsvStatesForGroup';
        const params = {
            groupGuid: groupGuid,
            beginDate: beginDate,
            endDate: endDate,
            localTimeZone: this._timeZone
        };
        this._requestService.post(url, null, params)
            .then(fileName => {
                window.open(this._requestService.getApiUrl() + fileName);
                defered.resolve();
            })
            .catch(reason =>
                defered.reject(reason));
        return defered.promise;
    }

    getCsvAggregatedValue(aggregatedValueDto: GetAggregatedValueDto, name: string): angular.IPromise<string> {
        const defered = this._$q.defer<string>();

        const url = `ExportToCsv/GetCsvAggregatedValue`;
        const params = {
            fileNamePrefix: name,
            localTimeZone: this._timeZone
        };

        this._requestService.postAtV1(url, aggregatedValueDto, params)
            .then(data => {
                window.open(this._requestService.getApiUrl() + data);
                defered.resolve();
            }).catch(reason => defered.reject(reason));

        return defered.promise;
    }

    getCsvFromData(dataArray: string[][], fileName: string, addDoubleQuotes: boolean = false) {
        const separator = ';';
        try {
            const rows = dataArray.map(row => {
                row = row.map(field => {
                    if (addDoubleQuotes) {
                        return `"${field?.toString().replace(/"/g, '""')}"`;
                    } else {
                        return typeof field === 'string' && field?.indexOf(separator) >= 0
                            ? `"${field.replace(/"/g, '""')}"`
                            : field;
                    }
                });
                return row.join(separator);
            });

            const data = rows.join('\n');
            this._uploadService.download(data, fileName, 'text/csv');
        } catch (e) {
            this._loggerService.logError(e, true);
        }
    }

    exportAggregatedToCsv(series: SimpleCsvSeries[], boundData: TagDateValue[], name: string): angular.IPromise<any> {
        // Same as below, but different, but still the same...
        let data = [];
        const formatter = this._formatNumberHelper.getFormatter(null, false);
        try {
            const filteredSeries = series;
            // #44103 : reproduce same format as before; not consistent with trends however
            const viewModels = filteredSeries
                .map(s => this.getBoundOrBaseSerie(s));
            const nameHeading = (viewModels.map((s) => s.name));
            const unitHeading = (viewModels.map((s) => s.unitName));
            const commentHeading = (viewModels.map((s) => s.shortComment));
            data = [nameHeading, commentHeading, unitHeading];
            const rows = this.convertToRows(boundData, series, formatter, false, viewModels);
            data = data.concat(rows);
        }
        catch (e) {
            console.error(e);
        }

        return this._$q.when(this.getCsvFromData(data, `${name}_${moment().format('YYYY-MM-DD HHmm')}.csv`));
    }

    exportToCsv(
        series: CsvSeries[],
        boundData: TagDateValue[],
        name: string,
        options?: CsvExportOptions
    ): angular.IPromise<any> {
        let data = [];
        const formatter = this._formatNumberHelper.getFormatter(null, false);
        try {
            // Filter missing range serie
            // recreate viewmodel based on bound serie if exist, otherwise use base serie
            const filteredSeries = series.filter(s => s.serieType !== 'range' && s.seriesType !== 'range');
            const viewModels = filteredSeries.map(s => this.getBoundOrBaseSerie(s));
            const headings = this.createHeadings(filteredSeries, viewModels);
            if (options) {
                this.addHeadingsFromOptions(options, data, headings);
            } else {
                data = headings.map(heading => heading);
            }
            const rows = this.convertToRows(boundData, series, formatter, true, viewModels);
            data = data.concat(rows);
        }
        catch (e) {
            console.error(e);
        }
        const filename = `${name}_${moment().format('YYYY-MM-DD HHmm')}.csv`;
        return this._$q.when(this.getCsvFromData(data, filename));
    }

    private createHeadings(filteredSeries: CsvSeries[], viewModels: IValueViewModel[]): string[][] {
        const headings: string[][] = [];
        headings.push(['TimeStamp', 'LocalTimeStamp'].concat(filteredSeries.map((_s, i) => (i + 1).toString())));
        headings.push(['Name', ''].concat(viewModels.map((s) => s.name)));
        headings.push(['Legend', ''].concat(filteredSeries.map((s) => s.legendText)));
        headings.push(['Group name', ''].concat(viewModels.map((s) => s.groupUniqueName)));
        headings.push(['Unit', ''].concat(viewModels.map((s) => s.unitName)));
        headings.push(['Short comment', ''].concat(viewModels.map((s) => s.shortComment)));
        // 36 is the length of a guid
        headings.push(['Value', ''].concat(filteredSeries.map(s => s.identifier.slice(0, -36))));
        return headings;
    }

    private addHeadingsFromOptions(options: CsvExportOptions, data: any[], headings: string[][]): void {
        if (options.hasFirstHeading) {
            data.push(headings[0]);
        } else {
            headings[1][1] = 'LocalTimeStamp';
        }
        data.push(headings[1]);
        if (options.hasLegend) {
            data.push(headings[2]);
        }
        if (options.hasGroupName) {
            data.push(headings[3]);
        }
        if (options.hasUnit) {
            data.push(headings[4]);
        }
        if (options.hasShortComment) {
            data.push(headings[5]);
        }
        if (options.hasValue) {
            data.push(headings[6]);
        }
    }

    private convertToRows(boundData: TagDateValue[], series: SimpleCsvSeries[], formatter: Intl.NumberFormat, hasDates: boolean, viewModels: IValueViewModel[]) {
        const byDate = d3.nest<TagDateValue>()
            .key(d => d.timeStamp as any)
            .entries(boundData)
            .sort((a, b) => a.key > b.key ? 1 : -1);
        return byDate.map(d => {
            let datesCol = [];
            if (hasDates) {
                const m = moment(d.key);
                datesCol = [m.clone().utc().format(Csvformat), m.format(Csvformat)];
            }
            return datesCol.concat(series.map(s => {
                const foundVm = viewModels?.find(viewModel => s.identifier.includes(viewModel.guid));
                const values = d.values.filter(v => s.identifier === s.selector(v));
                return values && values.length
                    ? this.getFormattedValue(values, foundVm, formatter)
                    : '';
            }));
        });
    }

    private getFormattedValue(values: any, foundVm: IValueViewModel, formatter: Intl.NumberFormat): string {
        let result = '';
        const v = values[0].value;
        const formattedValue = values[0].comment;
        if (!!v || v === 0) {
            if (foundVm
                && (foundVm.actualDataType === ActualDataType.Digital
                    || foundVm.actualDataType === ActualDataType.String)) {
                result = formattedValue;
            } else if (foundVm && foundVm.actualDataType === ActualDataType.Timestamp) {
                const manualMoment = moment.unix(v);
                result = manualMoment.format(format);
            } else {
                result = formatValueForCsv(v, formatter);
            }
        }
        return result;
    }

    // data: IvalueViewModel[] | IIdentifierVm[] | tagDefinitionVm[] | flatAlarmRowViewModels[]
    getCsvContent(
        columns: IDisplayedColumn[],
        data: any[],
        propertyMapper: IMappingDictionary<any>,
        isGuidNotExported: boolean = false
    ): string[][] {
        // Handle guid property
        const hasGuidProperty = this.hasGuidProperty(columns);
        let headers = (hasGuidProperty || isGuidNotExported)
            ? columns.map(d => d.label)
            : ['guid'].concat(columns.map(d => d.label));

        // Handle user fields
        headers = headers
            .filter(v => v.toLocaleLowerCase() !== 'own fields')
            .concat(this.getUserFieldsHeaders(data));

        const content = [headers];
        const values = data.map(i => {
            let val = (hasGuidProperty || isGuidNotExported)
                ? columns.filter(v => v.property !== 'customProperties').map(d => propertyMapper[d.property](i))
                : [i.guid].concat(columns.filter(v => v.property !== 'customProperties').map(d => propertyMapper[d.property](i)));
            if (i.customProperties && Object.keys(i.customProperties).length > 0) {
                val = val.concat(this.getUserFieldsValues(i.customProperties));
            }
            return val;
        });
        return content.concat(values);
    }

    hasGuidProperty(exportedFields: IDisplayedColumn[]): boolean {
        let hasGuidProperty = false;
        if (_.some(exportedFields, v => v.property.toLocaleLowerCase() === 'guid')) {
            hasGuidProperty = true;
        }
        return hasGuidProperty;
    }

    hasExportCustomProperties(exportedFields: IDisplayedColumn[]): boolean {
        let hasCustomProperties = false;
        if (_.some(exportedFields, v => v.property.toLocaleLowerCase() === 'customproperties')) {
            hasCustomProperties = true;
        }
        return hasCustomProperties;
    }

    getUserFieldsHeaders(data: { customProperties?: any }[]): string[] {
        const ownFieldsHeaders: string[] = [];
        const withCustomProperties = data.filter(i => i.customProperties && Object.keys(i.customProperties).length > 0);
        if (withCustomProperties && withCustomProperties.length > 0) {
            const maxUserFields = Object.keys(_.reverse(_.sortBy(withCustomProperties, i => Object.keys(i.customProperties).length))[0].customProperties).length;
            for (let i = 1; i <= maxUserFields; i++) {
                ownFieldsHeaders.push(`Field${i} name`);
                ownFieldsHeaders.push(`Field${i} value`);
            }
        }
        return ownFieldsHeaders;
    }

    getUserFieldsValues(customProperties: { [key: string]: string }): string[] {
        const values: string[] = [];
        _.sortBy(Object.keys(customProperties), k => k).forEach(key => {
            values.push(key);
            values.push(customProperties[key]);
        });
        return values;
    }

    private getBoundOrBaseSerie(s: SimpleCsvSeries): IValueViewModel {
        return (s.attributes && s.attributes.item)
            ? JSON.parse(s.attributes.item)
            : s;
    }
}